// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import PlusCircle from "@insite/mobius/Icons/PlusCircle";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { partNumbersStyle } from "@west/App/ComponentExtensions/Product/ProductPartNumbersExtensions";
import { getNotifyStyle } from "@west/App/Components/Notify";
import { FormFieldLabel, FormInputWrapper } from "@west/MobiusOverrides/FormField";
import { ProductAvailabilityStyles } from "@west/Overrides/Components/ProductAvailability";
import { CartLineQuantityStyles } from "@west/Overrides/Widgets/Cart/CartLineQuantity";
import Edit from "@west/XDL/Icons/Edit";
import Remove from "@west/XDL/Icons/Remove";
import { fontConfig } from "@west/XDL/PreStyle/typography";
import { color, ellipsis, mergeWithCss, xScreen } from "@west/XDL/Utilities";
import { css } from "styled-components";

export interface CartLineSharedStyles {
    productImage?: ProductImageStyles;
    productDescription?: ProductDescriptionStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    productNonReturnableMessage?: TypographyPresentationProps;
    addToListLink?: LinkPresentationProps;
    quantityHeadingAndText?: SmallHeadingAndTextStyles;
    quantityTextField?: TextFieldPresentationProps;
    qtyLabel?: TypographyProps;
    quantity?: CartLineQuantityStyles;
    productPrice?: ProductPriceStyles;
    extendedUnitNetPriceText?: SmallHeadingAndTextStyles;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeValueText?: TypographyPresentationProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    promotionsContainer?: InjectableCss;
    promotionNameLabel?: TypographyPresentationProps;
    promotionNameText?: InjectableCss;
    removeCartLineIcon?: IconPresentationProps;
    cartLineNotes?: CartLineNotesStyles;
    cartLineErrorMessageGridItem?: GridItemProps;
    errorMessage?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    configurationContainer?: InjectableCss;
    configurationOptionText?: TypographyProps;
    configurationOptionLabel?: TypographyProps;
    unitOfMeasure?: TypographyProps;
    qtyInputStyle?: TextFieldPresentationProps;
}

const cartLineSharedStyles: CartLineSharedStyles = {
    productImage: {
        linkWrappingImage: {
            css: css`
                width: 128px;
                height: 128px;
                background-color: ${color("common.white")};
                justify-content: center;
                ${xScreen("lte", "md")} {
                    width: 70px;
                    height: 70px;
                }
            `,
        },
        img: {
            css: css`
                max-width: 100%;
                max-height: 100%;
                height: auto;
                object-fit: contain;
            `,
        },
    },
    productDescription: {
        productDetailLink: {
            color: "text",
            typographyProps: {
                variant: "headerSecondary",
                forwardAs: "p",
                css: css`
                    letter-spacing: 0;
                    line-height: 16px;
                `,
            },
            css: css`
                display: inline-block;
                align-self: flex-start;
                min-height: 32px;
                height: auto;
                ${ellipsis(2)}
                @media print {
                    display: block;
                    width: 100%;
                }
            `,
        },
        productDetailDescription: {
            size: 14,
            weight: fontConfig.weight.bold,
            css: css`
                letter-spacing: 0;
                line-height: 15px;
                height: auto;
                @media print {
                    display: block;
                    width: 100%;
                }
            `,
        },
    },
    productPartNumbers: {
        container: {
            css: css`
                ${xScreen("lte", "md")} {
                    > div {
                        gap: 0 22px;
                    }
                }
                @media print {
                    display: block;
                    width: 100%;
                }
            `,
        },
        erpNumberGridItem: {
            css: css`
                flex-basis: auto;
                word-break: keep-all;
            `,
        },
    },
    productNonReturnableMessage: {
        size: 12,
        lineHeight: "22px",
        color: "common.ui5",
        css: css`
            margin-top: 4px;
        `,
    },
    productPrice: {
        priceLabelText: {
            size: 12,
            weight: fontConfig.weight.regular,
            css: css`
                line-height: 13px;
                text-transform: capitalize;
                padding: 0;
                margin-bottom: 4px;
                display: none;
                ${xScreen("lte", "md")} {
                    display: block;
                }
                @media print {
                    &&& {
                        font-size: 14px;
                        line-height: 16px;
                    }
                }
            `,
        },
        priceWrapper: {
            css: css`
                display: flex;
                align-items: flex-end;
            `,
        },
        price: {
            priceText: {
                variant: "h6",
                forwardAs: "span",
                css: css`
                    line-height: 21px;
                    margin-bottom: 0;
                    display: inline;
                `,
            },
            unitOfMeasure: {
                unitOfMeasureText: {
                    size: 12,
                    lineHeight: "16px",
                    css: css`
                        white-space: nowrap;
                        padding-bottom: 1px;
                    `,
                },
            },
        },
    },
    extendedUnitNetPriceText: {
        extendedStyles: {
            heading: {
                size: 12,
                lineHeight: "13px",
                weight: fontConfig.weight.regular,
                css: css`
                    line-height: 13px;
                    text-transform: capitalize;
                    padding: 0;
                    margin-bottom: 4px;
                    display: none;
                    ${xScreen("lte", "md")} {
                        display: block;
                    }
                    @media print {
                        &&& {
                            font-size: 14px;
                            line-height: 16px;
                        }
                    }
                `,
            },
            text: {
                variant: "h6",
                forwardAs: "p",
                css: css`
                    line-height: 21px;
                    text-align: start;
                    display: flex;
                    margin-bottom: 0;
                `,
            },
            wrapper: {
                css: css`
                    width: fit-content;
                `,
            },
        },
    },
    addToListLink: {
        typographyProps: {
            size: 12,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 14px;
            `,
        },
        icon: {
            iconProps: {
                src: PlusCircle,
                size: 12,
            },
            position: "left",
        },
    },
    costCodeGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    costCodeLabelText: {
        ...partNumbersStyle.label,
    },
    costCodeValueText: {
        ...partNumbersStyle.value,
    },
    configurationContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 4px;
        `,
    },
    configurationOptionText: {
        ...partNumbersStyle.value,
    },
    configurationOptionLabel: {
        ...partNumbersStyle.label,
    },
    promotionsContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 10px;
        `,
    },
    promotionNameLabel: {
        ...partNumbersStyle.label,
    },
    promotionNameText: {
        ...partNumbersStyle.value,
    },
    qtyLabel: {
        as: "label",
        size: 12,
        weight: fontConfig.weight.regular,
        lineHeight: "13px",
        css: css`
            margin-bottom: 4px;
            display: none;
            ${xScreen("lte", "md")} {
                display: block;
            }
            @media print {
                &&& {
                    font-size: 14px;
                    line-height: 16px;
                }
            }
        `,
    },

    removeCartLineIcon: {
        src: Remove,
        size: 11,
        color: "common.ui5",
        css: css`
            &:hover {
                color: ${color("danger")};
            }
            &,
            & > svg {
                height: 13px;
            }
        `,
    },
    cartLineNotes: {
        wrapper: {
            css: css`
                width: auto;
                ${xScreen("lte", "md")} {
                    width: auto;
                }
            `,
        },
        editableNotesTextArea: {
            cssOverrides: {
                inputSelect: css`
                    max-height: 150px;
                `,
            },
        },
        toggleLink: {
            typographyProps: {
                size: 12,
                weight: "bold",
                css: css`
                    letter-spacing: 0;
                    line-height: 14px;
                `,
            },
            icon: {
                iconProps: {
                    src: Edit,
                    size: 12,
                },
                position: "left",
            },
        },
    },
    cartLineErrorMessageGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 4px;
            ${xScreen("lte", "md")} {
                margin-top: 10px;
            }
        `,
    },
    errorMessage: {
        ...mergeWithCss(getNotifyStyle("danger"), {
            css: css`
                margin: 0;
            `,
        }),
    },

    unitOfMeasure: {
        size: 12,
        weight: fontConfig.weight.regular,
        css: css`
            line-height: 13px;
            text-transform: capitalize;
        `,
    },
    qtyInputStyle: {
        labelProps: {
            css: css`
                display: none;
                ${xScreen("lte", "md")} {
                    display: block;
                }
                text-wrap: nowrap;
                @media print {
                    &&&& {
                        background-color: transparent !important;
                        position: relative !important;
                        display: block !important;
                        text-wrap: nowrap !important;
                        font-size: 14px;
                        line-height: 16px;
                        left: 40px !important;
                    }
                }
            `,
        },
        cssOverrides: {
            formField: css`
                max-width: 80px;
            `,
            formInputWrapper: css`
                @media print {
                    &&& {
                        max-width: 80px !important;
                    }
                }
            `,
        },
    },
};

export default cartLineSharedStyles;
